import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import TabWithFilter from '@/components/TabWithFilter';
import WithdrawModal from '@/components/WithdrawModal';
import WithdrawDetailsModal from '@/components/WithdrawDetailsModal';
import BenefitDetailsModal from '@/components/BenefitDetailsModal';
import Button from '@/components/Button';
import PenaltyRestrictionDialog from '@/components/PenaltyRestrictionDialog';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { getWalletBalance, getFinanceActivities, getWithdrawalDetails } from '@/api/finance';
import { formatCurrency } from '@/utils/formatUtils';
import { showToast } from '@/utils/toastManager';
import dondonGreyLogo from '@/assets/dondon_grey.svg';
import sellIcon from '@/assets/sell.svg';
import withdrawQuestionIcon from '@/assets/withdraw_question.svg';
import usePenaltyGuard from '@/hooks/usePenaltyGuard';
import ConfirmDialog from '@/components/ConfirmDialog';
import questionIcon from '@/assets/question.svg';

const WalletPage = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);
  const [isWithdrawDetailsModalOpen, setIsWithdrawDetailsModalOpen] = useState(false);
  const [isBenefitDetailsModalOpen, setIsBenefitDetailsModalOpen] = useState(false);
  const [selectedWithdrawalId, setSelectedWithdrawalId] = useState(null);
  const [selectedBenefitData, setSelectedBenefitData] = useState(null);
  const { ensureActionAllowed, penaltyDialogProps } = usePenaltyGuard();
  const [isBenefitHelpOpen, setIsBenefitHelpOpen] = useState(false);

  // Check URL parameter to auto-open withdraw modal
  useEffect(() => {
    const shouldOpenWithdraw = searchParams.get('withdraw') === 'true';
    if (shouldOpenWithdraw) {
      if (ensureActionAllowed()) {
        setIsWithdrawModalOpen(true);
      }
      // Remove the parameter from URL to clean it up
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('withdraw');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, ensureActionAllowed]);

  const { data: balanceData, isLoading: isLoadingBalance } = useQuery({
    queryKey: ['walletBalance'],
    queryFn: getWalletBalance
  });

  // Get the API type based on active tab
  const getApiType = (tab) => {
    switch (tab) {
      case 'sales':
        return 'REVENUE';
      case 'withdrawals':
        return 'WITHDRAWAL_REQUEST';
      default:
        return 'ALL';
    }
  };

  const { 
    data: activitiesData, 
    isLoading: isLoadingActivities,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQuery({
    queryKey: ['financeActivities', activeTab, dateRange.startDate, dateRange.endDate],
    queryFn: ({ pageParam }) => getFinanceActivities({
      type: getApiType(activeTab),
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      cursor: pageParam
    }),
    initialPageParam: null,
    getNextPageParam: (lastPage) => {
      // 如果返回的数据小于20条，说明没有下一页
      if (lastPage.data.length < 20) {
        return undefined;
      }
      // 返回下一页的cursor
      return lastPage.cursor;
    }
  });

  // Query for withdrawal details
  const { data: withdrawalDetailsData } = useQuery({
    queryKey: ['withdrawalDetails', selectedWithdrawalId],
    queryFn: () => getWithdrawalDetails(selectedWithdrawalId),
    enabled: !!selectedWithdrawalId && isWithdrawDetailsModalOpen
  });

  // Get withdrawable balance from API data
  const currentBalance = balanceData?.data?.withdrawable || 0;
  const hasWithdrawableAmount = currentBalance > 0;
  
  // Get detailed balance information
  const orderRevenue = balanceData?.data?.orderRevenue || 0;
  const benefitFee = balanceData?.data?.benefitFee || 0;

  // Get activities from API data (flatten all pages)
  const activities = activitiesData?.pages?.flatMap(page => page.data) || [];

  const tabs = [
    { value: 'all', label: t('wallet.tabs.all') },
    { value: 'sales', label: t('wallet.tabs.sales') },
    { value: 'withdrawals', label: t('wallet.tabs.withdrawals') }
  ];

  const handleSearch = (newDateRange) => {
    setDateRange(newDateRange);
  };

  const handleReset = () => {
    setDateRange({ startDate: null, endDate: null });
  };

  const handleDetailsClick = (activity) => {
    if (activity.source === 'BENEFIT_FEE') {
      // Show benefit details modal
      setSelectedBenefitData(activity);
      setIsBenefitDetailsModalOpen(true);
    } else if (activity.type === 'WITHDRAWAL_REQUEST') {
      setSelectedWithdrawalId(activity.id);
      setIsWithdrawDetailsModalOpen(true);
    } else if (activity.type === 'REVENUE') {
      // Navigate to OrdersPage with the order ID
      navigate(`/me/sales?saleId=${activity.orderId}`);
    }
  };

  // Format activity data for display
  const formatActivityName = (activity) => {
    // Check if it's a benefit fee
    if (activity.source === 'BENEFIT_FEE') {
      return activity.status === 'DEDUCTED' ? t('wallet.benefitDetails.deducted') : t('wallet.benefitDetails.granted');
    }
    
    if (activity.type === 'REVENUE') {
      return t('wallet.activity.revenue');
    } else if (activity.type === 'WITHDRAWAL_REQUEST') {
      if (activity.status === 'REJECTED') {
        return `${t('wallet.rejection.message')}(${activity.rejectionReason})`;
      }
      return t('wallet.activity.withdrawal');
    }
    return activity.type;
  };

  const formatActivityDate = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${year}/${month}/${day} ${hours}:${minutes}`;
  };

  const formatActivityAmount = (activity) => {
    const amount = activity.amount.toLocaleString('ja-JP') + '円';
    if (activity.source === 'BENEFIT_FEE' && activity.status === 'DEDUCTED') {
        return amount;
    } else if (activity.type === 'REVENUE') {
      return `+${amount}`;
    } else if (activity.type === 'WITHDRAWAL_REQUEST') {
      if (activity.status === 'REJECTED') {
        return amount; // No +/- for rejected withdrawals
      }
      return `-${amount}`;
    }
    return amount;
  };

  const getActivityAmountStyle = (activity) => {
    if ((activity.type === 'WITHDRAWAL_REQUEST' && activity.status === 'REJECTED') || (activity.source === 'BENEFIT_FEE' && activity.status === 'DEDUCTED')) {
      return {
        textDecoration: 'line-through'
      };
    }
    return {};
  };

  const getActivityNameStyle = (activity) => {
    if ((activity.type === 'WITHDRAWAL_REQUEST' && activity.status === 'REJECTED') || (activity.source === 'BENEFIT_FEE' && activity.status === 'DEDUCTED')) {
      return {
        color: '#FF5601'
      };
    }
    return {};
  };

  const getActivityDescription = (activity) => {
    return formatActivityDate(activity.createdAt);
  };

  return (
    <div className="mx-auto">
      {/* Current Balance Section */}
      <div className="w-full p-6 bg-white rounded-md flex flex-col justify-start items-start overflow-hidden mb-6">
        {/* Header */}
        <h1 className="text-lg font-bold mb-6">{t('me.menu.wallet')}</h1>
        <div className="self-stretch flex flex-col justify-start items-start">
          <div className="text-[#aaaaaa] text-sm font-normal">
            {t('wallet.currentBalance')}
          </div>
          <div className="w-full inline-flex justify-between items-center">
            <div className="text-black text-[40px] font-medium">
              {isLoadingBalance ? (
                <span className="text-gray-300">¥ --</span>
              ) : (
                formatCurrency(currentBalance)
              )}
            </div>
            <Button
              onClick={(e) => {
                if (!hasWithdrawableAmount || currentBalance < 1000) {
                  e.preventDefault();
                  showToast(t('wallet.minimumWithdraw.message'));
                  return;
                }
                if (ensureActionAllowed()) {
                  setIsWithdrawModalOpen(true);
                }
              }}
              variant="primary"
              size="l"
              className={!hasWithdrawableAmount || currentBalance < 1000 ? 'bg-[#EEEEEE] !text-[#AAAAAA] cursor-not-allowed hover:bg-[#EEEEEE]' : ''}
            >
              {t('wallet.withdrawRequest')}
            </Button>
          </div>
          
          {/* Balance Details */}
          <div className="mt-3 flex items-center gap-2">
            <span style={{ fontFamily: 'Noto Sans JP', fontWeight: 400, fontSize: '14px', color: '#AAAAAA', display: 'flex', alignItems: 'center' }}>
              {t('wallet.balanceDetails.orderRevenue')}
            </span>
            <span style={{ fontFamily: 'Roboto', fontWeight: 400, fontSize: '14px', color: '#000000', display: 'flex', alignItems: 'center' }}>
              {isLoadingBalance ? '¥ --' : `¥ ${orderRevenue.toLocaleString('ja-JP')}`}
            </span>
            <div style={{ width: '1px', height: '12px', backgroundColor: '#D9D9D9' }}></div>
            <span style={{ fontFamily: 'Noto Sans JP', fontWeight: 400, fontSize: '14px', color: '#AAAAAA', display: 'flex', alignItems: 'center' }}>
              {t('wallet.balanceDetails.benefitReward')}
            </span>
            <img 
              src={withdrawQuestionIcon} 
              alt="Question" 
              className="w-[14.3px] h-[14.3px] cursor-pointer"
              style={{ color: '#AAAAAA' }}
              onClick={() => setIsBenefitHelpOpen(true)}
            />
            <span style={{ fontFamily: 'Roboto', fontWeight: 400, fontSize: '14px', color: '#000000', display: 'flex', alignItems: 'center' }}>
              {isLoadingBalance ? '¥ --' : `¥ ${benefitFee.toLocaleString('ja-JP')}`}
            </span>
          </div>
        </div>
      </div>
      
      {/* Transactions Section */}
      <div className="w-full p-6 bg-white rounded-md flex flex-col justify-start items-start overflow-hidden">
        <h2 className="text-lg font-bold mb-6">{t('wallet.transactionHistory')}</h2>
        
        <div className="w-full flex flex-col">
          <TabWithFilter
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={(tab) => {
              setActiveTab(tab);
              setDateRange({ startDate: null, endDate: null }); // Reset date range when tab changes
            }}
            onSearch={handleSearch}
            onReset={handleReset}
          />
          
          {/* Table Header */}
          {!isLoadingActivities && activities.length > 0 && (
            <div className="mt-4 grid grid-cols-[1fr,180px,180px] gap-6 py-1.5 bg-[#f8f8f8] text-sm text-center text-gray-500">
              <div>{t('wallet.table.item')}</div>
              <div>{t('wallet.table.amount')}</div>
              <div>{t('wallet.table.management')}</div>
            </div>
          )}
            
          {/* Table Content - Activity Rows */}
          {isLoadingActivities ? (
            <div className="py-10 w-full h-[400px] flex justify-center items-center text-[#aaaaaa]">
              {t('common.loading')}
            </div>
          ) : activities.length > 0 ? (
            <div className="divide-y divide-gray-200">
              {activities.map(activity => (
                <div key={activity.id} className="grid grid-cols-[1fr,180px,180px] gap-6 py-6">
                  <div className="inline-flex justify-start items-center gap-4">
                    <div className="w-12 h-12 bg-[#eeeeee] rounded-md flex justify-center items-center">
                      <img src={sellIcon} alt="Activity" className="w-5 h-5" />
                    </div>
                    <div className="inline-flex flex-col justify-start items-start gap-1">
                      <div className="text-sm font-medium" style={getActivityNameStyle(activity)}>{formatActivityName(activity)}</div>
                      <div className="text-xs text-[#aaaaaa]">{getActivityDescription(activity)}</div>
                    </div>
                  </div>

                  <div className="text-base font-medium text-center flex justify-center items-center" style={getActivityAmountStyle(activity)}>
                    {formatActivityAmount(activity)}
                  </div>

                  <div className="mr-4 flex justify-center items-center">
                    <Button
                      onClick={() => handleDetailsClick(activity)}
                      variant="default"
                      size="s"
                    >
                      {t('wallet.action.details')}
                    </Button>
                  </div>
                </div>
              ))}
              
              {/* Load More Button */}
              {hasNextPage && (
                <div className="py-6 flex justify-center">
                  <Button
                    onClick={() => fetchNextPage()}
                    variant="default"
                    size="m"
                    disabled={isFetchingNextPage}
                  >
                    {isFetchingNextPage ? t('common.loading') : t('common.loadMore')}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className='w-full h-[400px] flex flex-col justify-center items-center gap-6'>
              <img src={dondonGreyLogo} alt="DonDon" className="w-24 h-16 opacity-50" />
              <div className="w-full flex justify-center items-center text-[#aaaaaa]">
                {t('wallet.noTransactions')}
              </div>
            </div>
          )}
        </div>
      </div>

      {isWithdrawModalOpen && (
        <WithdrawModal
          isOpen={isWithdrawModalOpen}
          onClose={() => setIsWithdrawModalOpen(false)}
        />
      )}

      {isWithdrawDetailsModalOpen && (
        <WithdrawDetailsModal
          isOpen={isWithdrawDetailsModalOpen}
          onClose={() => {
            setIsWithdrawDetailsModalOpen(false);
            setSelectedWithdrawalId(null);
          }}
          withdrawalData={withdrawalDetailsData?.data}
        />
      )}

      {isBenefitDetailsModalOpen && (
        <BenefitDetailsModal
          isOpen={isBenefitDetailsModalOpen}
          onClose={() => {
            setIsBenefitDetailsModalOpen(false);
            setSelectedBenefitData(null);
          }}
          benefitData={selectedBenefitData}
        />
      )}
      <ConfirmDialog
        isOpen={isBenefitHelpOpen}
        onOpenChange={setIsBenefitHelpOpen}
        onConfirm={() => setIsBenefitHelpOpen(false)}
        title={t('wallet.benefitHelp.title')}
        description={t('wallet.benefitHelp.description')}
        confirmText={t('common.confirm')}
        icon={questionIcon}
      />
      <PenaltyRestrictionDialog {...penaltyDialogProps} />
    </div>
  );
};

export default WalletPage;
